import { Box, Grid, SxProps, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useTranslation } from "react-i18next";
import {
  BILLING_PERIODS,
  BillingPeriod,
  annualSavings,
  annualSavingsPercent,
  monthlyRateFor,
  priceFor,
} from "src/business/payment/billingPeriod";
import { SubscriptionTier } from "src/data/landing/queries/subscriptionTiers";
import { formatPrice } from "src/Utils/number";
import {
  colorAccent,
  colorBorder,
  colorSurface,
  colorSurfaceSuccess,
  colorTextMuted,
  colorTextPrimary,
  colorTextSuccess,
  radiusCard,
  radiusChip,
} from "src/design/tokens";

interface BillingPeriodSelectorProps {
  /** The plan being paid for; `undefined` until a tier is picked. */
  tier: SubscriptionTier | undefined;
  value: BillingPeriod;
  onChange: (period: BillingPeriod) => void;
  disabled?: boolean;
  sx?: SxProps;
}

/**
 * The one money-shaped choice a declaration carries: a month at the monthly
 * price, or a year at the discounted rate.
 *
 * Both totals are shown side by side rather than behind a toggle — the annual
 * option only makes sense against the monthly one it undercuts, and the saving
 * is the reason to read the card at all. The figures mirror the backend's
 * derivation, so what is shown here is what the created payment reports back.
 */
export const BillingPeriodSelector = ({
  tier,
  value,
  onChange,
  disabled = false,
  sx,
}: BillingPeriodSelectorProps) => {
  const { t, i18n } = useTranslation();

  const savings = tier ? annualSavings(tier) : 0;
  const savingsPercent = tier ? annualSavingsPercent(tier) : 0;

  return (
    <Box sx={sx}>
      <Typography variant="caption" sx={{ color: colorTextMuted }}>
        {t("payments.declare.periodLabel")}
      </Typography>

      <Grid container spacing={1.5} sx={{ mt: 0 }}>
        {BILLING_PERIODS.map((period) => {
          const selected = period === value;
          const showSaving = period === "ANNUAL" && savings > 0;

          return (
            <Grid item xs={12} md={6} key={period}>
              <Box
                role="radio"
                aria-checked={selected}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                onClick={() => !disabled && onChange(period)}
                onKeyDown={(event) => {
                  if (disabled) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onChange(period);
                  }
                }}
                sx={{
                  height: "100%",
                  p: 2,
                  borderRadius: radiusCard,
                  border: `${selected ? 2 : 1}px solid ${
                    selected ? colorAccent : colorBorder
                  }`,
                  backgroundColor: selected ? colorSurfaceSuccess : colorSurface,
                  cursor: disabled ? "default" : "pointer",
                  opacity: disabled ? 0.6 : 1,
                  transition:
                    "border-color 0.15s ease, background-color 0.15s ease",
                  "&:hover": {
                    borderColor: disabled ? undefined : colorAccent,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  {selected ? (
                    <CheckCircleIcon
                      sx={{ fontSize: 18, color: colorAccent }}
                    />
                  ) : (
                    <RadioButtonUncheckedIcon
                      sx={{ fontSize: 18, color: colorTextMuted }}
                    />
                  )}
                  <Typography
                    sx={{ fontWeight: 600, color: colorTextPrimary }}
                  >
                    {t(`payments.period.${period}`)}
                  </Typography>
                  {showSaving && (
                    <Box
                      sx={{
                        ml: "auto",
                        px: 1,
                        py: 0.25,
                        borderRadius: radiusChip,
                        backgroundColor: colorAccent,
                        color: colorSurface,
                        fontSize: 11,
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("payments.declare.savePercent", {
                        percent: savingsPercent,
                      })}
                    </Box>
                  )}
                </Box>

                <Typography
                  sx={{ fontWeight: 700, fontSize: 22, color: colorTextPrimary }}
                >
                  {tier
                    ? t("payments.amount", {
                        amount: formatPrice(
                          priceFor(tier, period),
                          i18n.language,
                        ),
                      })
                    : "—"}
                </Typography>

                <Typography variant="caption" sx={{ color: colorTextMuted }}>
                  {tier
                    ? t("payments.declare.perMonth", {
                        amount: formatPrice(
                          monthlyRateFor(tier, period),
                          i18n.language,
                        ),
                      })
                    : t(`payments.period.${period}`)}
                </Typography>

                {showSaving && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: colorTextSuccess, mt: 0.5 }}
                  >
                    {t("payments.declare.saveAmount", {
                      amount: formatPrice(savings, i18n.language),
                    })}
                  </Typography>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
